import { create } from 'zustand';
import type { IngestFreshnessSnapshot, ServiceInstanceMetrics } from '../api/types';

const EMPTY_SERVICES: Record<string, ServiceInstanceMetrics[]> = {};

type MetricsState = {
  /** Per-service instance metrics, keyed by service name. */
  services: Record<string, ServiceInstanceMetrics[]>;
  /** Server-computed ingest freshness (2026-08-04 warning re-keying) —
   *  the freshness-primary health signal; null until the gateway's first
   *  poll lands. */
  freshness: IngestFreshnessSnapshot | null;
  lastUpdateAt: number | null;
  setServices: (s: Record<string, ServiceInstanceMetrics[]>) => void;
  setFreshness: (f: IngestFreshnessSnapshot) => void;
};

export const useMetricsStore = create<MetricsState>()((set) => ({
  services: EMPTY_SERVICES,
  freshness: null,
  lastUpdateAt: null,
  setServices: (incoming) =>
    set({
      services: incoming,
      lastUpdateAt: Date.now(),
    }),
  setFreshness: (freshness) => set({ freshness }),
}));

/** Get instances for a specific service. Returns stable empty array if none. */
const EMPTY_INSTANCES: ServiceInstanceMetrics[] = [];
export function useServiceInstances(service: string): ServiceInstanceMetrics[] {
  return useMetricsStore((s) => s.services[service] ?? EMPTY_INSTANCES);
}

/** Format bytes to human-readable string. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}K`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)}M`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}G`;
}

/** Format uptime ms to human-readable string. */
export function formatUptime(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}
